/**
 * MACD (Moving Average Convergence/Divergence)
 *
 * Classic trend-following momentum indicator.
 * Plots: MACD line (fast EMA − slow EMA), Signal line (EMA of MACD),
 * and Histogram (MACD − Signal).
 * Default params: fast=12, slow=26, signal=9.
 *
 * Category: Momentum | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { getSource, computeEMA } from "./sma";

export const macdIndicator: IndicatorDefinition = {
  id: "macd",
  name: "MACD",
  shortName: "MACD",
  category: "momentum",
  pane: "separate",
  params: [
    { key: "fast", label: "Fast Period", type: "number", default: 12, min: 1, max: 200 },
    { key: "slow", label: "Slow Period", type: "number", default: 26, min: 1, max: 200 },
    { key: "signal", label: "Signal Period", type: "number", default: 9, min: 1, max: 100 },
    { key: "source", label: "Source", type: "select", default: "close", options: [
      { label: "Close", value: "close" }, { label: "HL2", value: "hl2" },
    ]},
  ],
  plots: [
    { key: "macd", label: "MACD", color: "#2196F3", style: "line", lineWidth: 2, priceScaleId: "macd" },
    { key: "signal", label: "Signal", color: "#FF5722", style: "line", lineWidth: 1, priceScaleId: "macd" },
    { key: "histogram", label: "Histogram", color: "#26A69A", style: "histogram", priceScaleId: "macd", histogramBase: 0 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { fast, slow, signal, source = "close" } = params;
    const src = getSource(candles, source);

    const fastEMA = computeEMA(src, fast);
    const slowEMA = computeEMA(src, slow);

    // MACD line = fastEMA - slowEMA
    const macdLine: number[] = [];
    for (let i = 0; i < src.length; i++) {
      if (isNaN(fastEMA[i]) || isNaN(slowEMA[i])) {
        macdLine.push(NaN);
      } else {
        macdLine.push(fastEMA[i] - slowEMA[i]);
      }
    }

    // Signal line = EMA of MACD line (skip NaN prefix)
    const validMacd = macdLine.filter(v => !isNaN(v));
    const signalEMA = computeEMA(validMacd, signal);

    // Map back to candle indices
    const firstValid = macdLine.findIndex(v => !isNaN(v));
    const macdData: { time: number; value: number }[] = [];
    const signalData: { time: number; value: number }[] = [];
    const histData: { time: number; value: number }[] = [];

    let validIdx = 0;
    for (let i = 0; i < macdLine.length; i++) {
      if (isNaN(macdLine[i])) continue;

      macdData.push({ time: candles[i].time, value: macdLine[i] });

      if (!isNaN(signalEMA[validIdx])) {
        signalData.push({ time: candles[i].time, value: signalEMA[validIdx] });
        histData.push({ time: candles[i].time, value: macdLine[i] - signalEMA[validIdx] });
      }
      validIdx++;
    }

    return [
      { key: "macd", data: macdData },
      { key: "signal", data: signalData },
      { key: "histogram", data: histData },
    ];
  },
};
